import {
    getScenarioStore,
    getXClientFlightsHeaderValue,
    SearchScenarioId,
    setLatestTraceId,
} from 'owa-search';
import { lazyLogResponseNeverRendered } from 'owa-search-instrumentation';
import { getLocalTime, SubstrateSearchScenario, SuggestionSet } from 'owa-search-service';
import getSuggestionQueryString from '../../selectors/getSuggestionQueryString';
import convertSubstrateSuggestionsResponseToSuggestionSet from './convertSubstrateSuggestionsResponseToSuggestionSet';
import substrateSuggestions from '../../services/substrateSuggestions';
/**
 * Gets suggestions from 3S.
 */
export default async function getSubstrateSuggestions(
    scenarioId: SearchScenarioId
): Promise<SuggestionSet> {
    const searchStore = getScenarioStore(scenarioId);

    const suggestionsRequestTime = new Date();

    const customHeaders = {
        'client-session-id': searchStore.searchSessionGuid,
        'x-client-localtime': getLocalTime(),
        'client-request-id': searchStore.latestQFRequestId,
        'X-Client-Flights': getXClientFlightsHeaderValue(scenarioId),
    };

    // Get 3S scenario ID based on search box scenario ID.
    const substrateSearchScenario = SubstrateSearchScenario.FileSuggestions;

    const queryString = getSuggestionQueryString(scenarioId);

    const substrateSuggestionsResponse = await substrateSuggestions(
        queryString /** queryString */,
        searchStore.nextSearchQueryId /** cvid */,
        customHeaders /** customheaders */,
        substrateSearchScenario /** substrateSearchScenario*/,
        searchStore.latestQFRequestId /** logicalId */
    );

    const emptySuggestionSet = {
        Suggestions: [],
        TraceId: searchStore.latestQFRequestId,
        IsComplete: true,
        RequestStart: suggestionsRequestTime,
    };

    /**
     * If we don't get a valid response, return empty suggestion set. We
     * defensively check for a falsey object, but also check if the response
     * is an array (as that's what the makeServiceRequest function returns when
     * there's a fetch error).
     */
    if (!substrateSuggestionsResponse || Array.isArray(substrateSuggestionsResponse)) {
        return emptySuggestionSet;
    }

    try {
        // Dispatch action to update latestTraceId in the store.
        setLatestTraceId(
            substrateSuggestionsResponse.Instrumentation.TraceId,
            scenarioId,
            true /* isQFTraceId */
        );

        // Parse response into SuggestionSet.
        const suggestionSet = substrateSuggestionsResponse.Groups
            ? convertSubstrateSuggestionsResponseToSuggestionSet(substrateSuggestionsResponse)
            : {
                  Suggestions: [],
                  TraceId: substrateSuggestionsResponse.Instrumentation.TraceId,
                  IsComplete: true,
                  RequestStart: suggestionsRequestTime,
              };

        /**
         * Check suggestionsRequestTime against displayedQfRequestTime,
         * and if newer, than return.
         *
         * If not, log "responseneverrendered" event and return whatever is currently
         * in the store (since those suggestions are newer).
         */
        const displayedQFRequestTime = searchStore.displayedQFRequestTime;
        if (
            !displayedQFRequestTime ||
            suggestionsRequestTime.getTime() > displayedQFRequestTime.getTime()
        ) {
            suggestionSet.RequestStart = suggestionsRequestTime;
            return suggestionSet;
        } else {
            lazyLogResponseNeverRendered.importAndExecute(
                <string>suggestionSet.TraceId,
                substrateSearchScenario
            );

            // Return most recent, valid suggestions (if any exist).
            return searchStore.currentSuggestions || emptySuggestionSet;
        }
    } catch (e) {
        /**
         * In some cases (i.e. 401 from server) we will not have a body so return
         * empty suggestion set
         */
        return emptySuggestionSet;
    }
}
